import { Recipe } from '../types';
import { z } from 'zod';

const ingredientSchema = z.object({
  name: z.string().min(1).transform(val => val.trim()),
  amount: z.string().min(1).transform(val => val.trim()),
  unit: z.string().transform(val => val?.trim().toLowerCase() || 'g'),
  notes: z.string().optional().transform(val => val?.trim() ?? '')
});

const recipeSchema = z.object({
  title: z.string().min(1).transform(val => val.trim()),
  description: z.string().min(1).transform(val => val.trim()),
  ingredients: z.array(ingredientSchema).min(1),
  instructions: z.array(z.string().min(1)).min(1),
  prepTime: z.string().min(1).transform(val => val.trim()),
  cookTime: z.string().min(1).transform(val => val.trim()),
  difficulty: z.string().min(1).transform(val => val.trim()),
  servings: z.number().int().positive(),
  totalTime: z.string().min(1).transform(val => val.trim()),
  notes: z.array(z.string()).default([])
});

function validateRecipeData(data: unknown): Recipe {
  const processed = {
    ...(data as Record<string, unknown>),
    ingredients: Array.isArray((data as any).ingredients)
      ? (data as any).ingredients.map((ing: any) => ({
          name: String(ing.name || '').trim(),
          amount: String(ing.amount || '0').trim(),
          unit: String(ing.unit || 'g').trim().toLowerCase(),
          notes: String(ing.notes || '').trim()
        }))
      : [],
    instructions: Array.isArray((data as any).instructions)
      ? (data as any).instructions.map((step: string) => String(step).trim())
      : [],
    servings: Number((data as any).servings) || 4,
    notes: Array.isArray((data as any).notes)
      ? (data as any).notes.map((note: string) => String(note).trim())
      : []
  };

  const result = recipeSchema.safeParse(processed);
  if (!result.success) {
    const errors = result.error.issues.map(issue => issue.message).join(', ');
    throw new Error(`Invalid recipe format: ${errors}`);
  }

  return result.data;
}

export async function generateRecipe(ingredients: string[]): Promise<Recipe> {
  if (!ingredients?.length) {
    throw new Error('Please provide at least one ingredient');
  }

  const cleanIngredients = ingredients
    .map(ing => ing.trim().toLowerCase())
    .filter(Boolean);

  if (cleanIngredients.length === 0) {
    throw new Error('Please provide valid ingredients');
  }

  const response = await fetch('/api/generate-recipe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ingredients: cleanIngredients })
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to generate recipe' }));
    throw new Error(err.error || 'Failed to generate recipe');
  }

  const data = await response.json();
  return validateRecipeData(data);
}
